import { useEffect, useMemo, useRef, useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { actions } from 'features/creativeFunctionality/redux';
import debounce from 'lodash.debounce';
import convertFilter from './convertFilter';
import { useCustomGamesConverter } from './useCustomGamesConverter';
import { ONLY_DIGITS_REGEX } from '../shared';

export const useCustomGamesModel = () => {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);

  const { actionProcessing: creativeActionProcessing } = useSelector(state => state.creativeFunctionality, shallowEqual);
  const { actionProcessingByProvider } = useSelector(state => state.casino, shallowEqual);

  const {
    input: { selectGameType, selectProvider, changeOrderedGames, setSearchString, handleSendSearchResult },
    output: {
      games,
      providers,
      filteredProviders,
      disabledProviders,
      allPositionedGames,
      gameTypes,
      total,
      selectedGameType,
      selectedProvider,
      hasGamesBeenChanged,
      previousGames,
      searchString,
    },
  } = useCustomGamesConverter();

  const actionProcessing = useMemo(
    () => Object.values(actionProcessingByProvider).some(Boolean) || creativeActionProcessing,
    [actionProcessingByProvider, creativeActionProcessing],
  );

  const handleRemoveGameOrder = useCallback(
    game => {
      changeOrderedGames(prev => {
        const idx = prev.findIndex(g => g.key === game.key);
        if (idx === -1) return prev;

        const updated = [...prev];
        updated[idx] = { ...game, isActive: false, position: 0 };
        return updated;
      });
    },
    [changeOrderedGames],
  );

  const handleSwitchGame = useCallback(
    game => {
      changeOrderedGames(prev => {
        const idx = prev.findIndex(g => g.key === game.key);
        if (idx === -1) return prev;

        const updated = [...prev];
        updated[idx] = { ...game, isActive: !game.isActive };
        return updated;
      });
    },
    [changeOrderedGames],
  );

  const debouncedChangeGameOrder = useMemo(
    () =>
      debounce(({ value, game }) => {
        if (!value) {
          handleRemoveGameOrder(game);
          return;
        }

        if (!ONLY_DIGITS_REGEX.test(value)) return;

        changeOrderedGames(prev => {
          const idx = prev.findIndex(g => g.key === game.key);
          if (idx !== -1) {
            const updated = [...prev];
            updated[idx] = { ...game, position: +value };
            return updated;
          }
          return [...prev, { ...game, position: +value, isActive: true }];
        });
      }, 200),
    [changeOrderedGames, handleRemoveGameOrder],
  );

  useEffect(() => {
    return () => debouncedChangeGameOrder.cancel();
  }, [debouncedChangeGameOrder]);

  const handleChangeGameOrder = useCallback(data => debouncedChangeGameOrder(data), [debouncedChangeGameOrder]);

  const handleSubmit = useCallback(() => {
    dispatch(
      actions.setCustomGames(
        convertFilter({ games: allPositionedGames, gameTypes, providers, disabledProviders }),
        allPositionedGames,
      ),
    );
  }, [dispatch, allPositionedGames, gameTypes, providers, disabledProviders]);

  const handleReturnBack = useCallback(() => {
    changeOrderedGames(previousGames);
  }, [changeOrderedGames, previousGames]);

  const scrollToTop = useCallback(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, []);

  const handleSelectProvider = useCallback(
    provider => {
      selectProvider(provider);
      scrollToTop();
    },
    [selectProvider, scrollToTop],
  );

  const handleSelectGameType = useCallback(
    gameType => {
      selectGameType(gameType);
      scrollToTop();
    },
    [selectGameType, scrollToTop],
  );

  useEffect(() => {
    dispatch(actions.getCustomGames());
  }, [dispatch]);

  return {
    input: {
      handleSelectGameType,
      handleSelectProvider,
      handleSubmit,
      handleReturnBack,
      handleChangeGameOrder,
      handleRemoveGameOrder,
      handleSwitchGame,
      setSearchString,
      handleSendSearchResult,
    },
    output: {
      gameTypes,
      games,
      providers: filteredProviders,
      selectedGameType,
      selectedProvider,
      returnBackDisabled: hasGamesBeenChanged,
      actionProcessing,
      total,
      scrollRef,
      searchString,
    },
  };
};
